import * as Sentry from '@sentry/vue'
import { isScoredSlideType } from '@ahaslides-product/plugins-api'
import Config from '@/config'
import { resolveMarketplaceApiUrl } from '@/utils/resolveMarketplaceApiUrl'

export const EMBED_APP_FLAG = 'AHA-42142-embed-app'
export const MARKETPLACE_FLAG = 'AHA-42884-new-slide-market-place'
export const MARKETPLACE_APP_ID = 'marketplace'

const SET_MANIFESTS = 'SET_MANIFESTS'
const SET_MARKETPLACE_SLIDE_TYPES = 'SET_MARKETPLACE_SLIDE_TYPES'
const SET_MARKETPLACE_LOADED = 'SET_MARKETPLACE_LOADED'

const _state = {
  manifests: [],
  marketplaceSlideTypes: [],
  marketplaceLoaded: false,
}

const _flattenManifests = (manifests) => {
  const types = []
  manifests.forEach((app) => {
    (app.slideTypes || []).forEach((slideType) => {
      if (slideType && slideType.type) {
        types.push({ ...slideType, appId: app.id, app })
      }
    })
  })
  return types
}

// The marketplace API URL for this page load. Normally the configured one; on a
// sandbox build carrying ?marketplaceSandboxId=<id> (or a previously stored id),
// the same path against that marketplace sandbox's origin. Inert everywhere else.
const _marketplaceApiUrl = () => resolveMarketplaceApiUrl(
  Config.slideTypesMarketplaceUrl,
  Config.audienceUrl,
)

// The marketplace origin (scheme + host) derived from the resolved API URL,
// used to absolutize the relative `audienceUrl` the /api/slide-types-audience
// endpoint ships (e.g. "/plugin/poll/audience"). Deriving it from the SAME
// resolved URL the catalogue was fetched from keeps the plugin iframes on the
// origin the catalogue came from.
const _marketplaceOrigin = () => {
  try {
    return new URL(_marketplaceApiUrl()).origin
  } catch {
    return ''
  }
}

// Normalize a /api/slide-types-audience payload entry into the FLAT shape the
// store's consumers expect (a top-level `type`, `setting`, and an ABSOLUTE
// `audienceUrl` — read by customSlideTypeByType → SlidePluginContent/Presentation).
//
// The endpoint nests the slide config under `ahaConfig` and ships a RELATIVE
// `audienceUrl`, so we lift `ahaConfig` to the top level and absolutize the URL
// against the marketplace origin. Shape-agnostic: a legacy flat entry (no
// `ahaConfig`) and an already-absolute `audienceUrl` both pass through unchanged.
//
// Only `marketplace/<slug>` plugin slides are kept — matching the previous
// behaviour where solely marketplace entries (the ones carrying a top-level
// `type`) reached the store. Native presenter types resolve through the built-in
// slideTypes registry instead, so they are intentionally excluded here.
const _normalizeMarketplace = (raw) => {
  const origin = _marketplaceOrigin()
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const { ahaConfig, ...topLevel } = entry
      const cfg = ahaConfig && typeof ahaConfig === 'object' ? ahaConfig : entry
      const audienceUrl = typeof cfg.audienceUrl === 'string' && cfg.audienceUrl.startsWith('/') && origin
        ? `${origin}${cfg.audienceUrl}`
        : cfg.audienceUrl
      // Merge top-level metadata (e.g. `name`) with the ahaConfig slide config so
      // BOTH survive; ahaConfig wins on overlap (type/slideType/setting/plugin).
      return {
        ...topLevel, ...cfg, plugin: entry.plugin ?? cfg.plugin, audienceUrl,
      }
    })
    .filter((t) => t && typeof t.type === 'string' && t.type.startsWith('marketplace/'))
}

const _getters = {
  manifests: (state) => state.manifests,
  marketplaceSlideTypes: (state) => state.marketplaceSlideTypes,
  customSlideTypes: (state) => {
    const flagTypes = _flattenManifests(state.manifests)
    const seen = new Set(flagTypes.map((t) => t.type))
    const marketplaceTypes = state.marketplaceSlideTypes
      .filter((t) => t && t.type && !seen.has(t.type))
      .map((t) => ({ ...t, appId: MARKETPLACE_APP_ID }))
    return [...flagTypes, ...marketplaceTypes]
  },
  customSlideTypeByType: (_, getters) => (type) => {
    if (!type) return null
    return getters.customSlideTypes.find((s) => s.type === type) || null
  },
  isScoredSlide: (_, getters) => (slide) => {
    if (!slide) return false
    return isScoredSlideType(getters.customSlideTypeByType(slide.type))
  },
}

const _parseManifests = (payload) => {
  if (!payload?.value) return []
  try {
    const parsed = typeof payload.value === 'string' ? JSON.parse(payload.value) : payload.value
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item) => item && typeof item === 'object')
  } catch (error) {
    Sentry.captureException(error, { tags: { source: 'embed-app.module.js', flag: EMBED_APP_FLAG } })
    return []
  }
}

const actions = {
  loadManifests({ commit, rootGetters }) {
    const variant = rootGetters['featureToggles/getVariant'](EMBED_APP_FLAG)
    if (!variant?.enabled) {
      commit(SET_MANIFESTS, [])
      return
    }

    const manifests = _parseManifests(variant.payload)
    commit(SET_MANIFESTS, manifests)
  },

  async loadMarketplaceSlideTypes({ commit, state, rootGetters }) {
    const enabled = rootGetters['featureToggles/isFeatureEnabled'](MARKETPLACE_FLAG)
    if (!enabled) {
      commit(SET_MARKETPLACE_SLIDE_TYPES, [])
      commit(SET_MARKETPLACE_LOADED, false)
      return
    }

    // The embed-app plugin re-dispatches this on every feature-toggle refresh
    // (Unleash `ready` plus each `updateContext`). Fetch the catalog at most
    // once per enabled session so a client that can't reach the endpoint does
    // not re-request it — and re-report it — on every refresh. Set the flag
    // before awaiting so concurrent dispatches don't race a second fetch.
    if (state.marketplaceLoaded) return
    commit(SET_MARKETPLACE_LOADED, true)

    try {
      const response = await fetch(_marketplaceApiUrl())
      if (!response.ok) throw new Error(`Marketplace fetch failed: ${response.status}`)
      const body = await response.json()
      const raw = Array.isArray(body?.slideTypes) ? body.slideTypes : []
      commit(SET_MARKETPLACE_SLIDE_TYPES, _normalizeMarketplace(raw))
    } catch (error) {
      // `TypeError: Failed to fetch` is a browser-level network failure — the
      // request never reached the server (unreachable CDN, offline client,
      // ad-blocker, or regional filtering). It's expected and already handled
      // gracefully below, so don't report it to Sentry; it's noise, not a bug.
      if (!(error instanceof TypeError)) {
        Sentry.captureException(error, { tags: { source: 'embed-app.module.js', flag: MARKETPLACE_FLAG } })
      }
      commit(SET_MARKETPLACE_SLIDE_TYPES, [])
    }
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_MANIFESTS](state, manifests) {
    state.manifests = manifests
  },
  [SET_MARKETPLACE_SLIDE_TYPES](state, slideTypes) {
    state.marketplaceSlideTypes = slideTypes
  },
  [SET_MARKETPLACE_LOADED](state, loaded) {
    state.marketplaceLoaded = loaded
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
